import React from 'react'
import { api } from '../api/client'
import { minutesToTime, timeToMinutes } from '../catalog/format'
import Field from '../screens/settings/Field'
import '../screens/settings/Settings.css'

interface TimePickerProps {
  serviceLengthId: string | null // the length booked: slots wait until there is one
  // What to say while there is no length yet.
  emptyHint?: string
  date: string
  start: number | null
  excludeId?: string
  error?: string
  onDateChange: (date: string) => void
  onStartChange: (start: number | null) => void
}

// TimePicker offers the free start times for a service length on a date, and
// any other time for when Jessica knows better than the suggestions.
export default function TimePicker({ serviceLengthId, emptyHint = 'Choose a treatment to see free times.', date, start, excludeId, error, onDateChange, onStartChange }: TimePickerProps) {
  const [starts, setStarts] = React.useState<number[] | null>(null)
  const [failed, setFailed] = React.useState(false)
  const [custom, setCustom] = React.useState(false)

  React.useEffect(() => {
    setStarts(null)
    setFailed(false)
    if (!serviceLengthId || !date) {
      return
    }
    let active = true
    api
      .getSlots(serviceLengthId, date, excludeId)
      .then((list) => active && setStarts(list))
      .catch(() => active && setFailed(true))
    return () => {
      active = false
    }
  }, [serviceLengthId, date, excludeId])

  // A chosen time that isn't among the free ones is shown in the time input.
  React.useEffect(() => {
    if (starts && start !== null && !starts.includes(start)) {
      setCustom(true)
    }
  }, [starts, start])

  return (
    <div className="booking-section">
      <Field id="booking-date" label="Date">
        <input
          id="booking-date"
          type="date"
          value={date}
          onChange={(e) => {
            onDateChange(e.target.value)
            onStartChange(null)
          }}
        />
      </Field>

      {!serviceLengthId ? (
        <p className="field-hint">{emptyHint}</p>
      ) : failed ? (
        <p className="field-hint">Couldn’t load free times. You can still choose a time.</p>
      ) : starts === null ? (
        <p className="field-hint">Finding free times…</p>
      ) : starts.length === 0 ? (
        <p className="field-hint">No free times on this day.</p>
      ) : (
        <div className="chip-group" role="group" aria-label="Free times">
          {starts.map((m) => (
            <button
              key={m}
              type="button"
              className="chip"
              aria-pressed={!custom && start === m}
              onClick={() => {
                setCustom(false)
                onStartChange(m)
              }}
            >
              {minutesToTime(m)}
            </button>
          ))}
        </div>
      )}

      {custom || failed || !serviceLengthId ? (
        <Field id="booking-time" label="Time">
          <input
            id="booking-time"
            type="time"
            step={300}
            value={start === null ? '' : minutesToTime(start)}
            onChange={(e) => onStartChange(timeToMinutes(e.target.value))}
          />
        </Field>
      ) : (
        <button type="button" className="btn btn-quiet btn-compact" onClick={() => setCustom(true)}>
          Choose another time
        </button>
      )}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
